import React from 'react';
import { CalendarDays, Clock3, PackageCheck, Phone, Store, UserRound } from 'lucide-react';

export const ATTENTION_LABELS = {
  HIGH_ATTENTION: 'Alta atención',
  REVIEW_RECOMMENDED: 'Revisar',
  ELEVATED: 'Atención elevada',
  WAITING_RESPONSE: 'Esperando respuesta',
  NORMAL: 'Seguimiento normal',
};

export const STAGE_LABELS = {
  NO_PACKAGE: 'Sin paquete',
  IN_WAREHOUSE: 'En almacén',
  IN_FASTMAX: 'En Fast Max',
  WITH_DRIVER: 'Con conductor',
  OUT_FOR_DELIVERY: 'En ruta',
  POSTPONED_WITH_DRIVER: 'Postergada con conductor',
  UNKNOWN: 'Etapa por revisar',
};

export const COMMUNICATION_LABELS = {
  NO_CONTACT: 'Sin contacto registrado',
  WAITING_RESPONSE: 'Esperando respuesta',
  CUSTOMER_REPLIED: 'Cliente respondió',
  NO_REPLY: 'Sin respuesta registrada',
};

export const REASON_LABELS = {
  NO_CUSTOMER_REPLY: 'Sin respuesta',
  WAITING_RESPONSE_WINDOW: 'Dentro de ventana',
  NO_CUSTOMER_CONTACT: 'Sin contacto',
  PRIOR_RECIPIENT_CANCEL: 'Cancelación previa',
  MULTIPLE_PRIOR_RECIPIENT_CANCELS: 'Varias cancelaciones previas',
  PRIOR_RECIPIENT_REJECTION_CONTEXT: 'Rechazo previo',
  STORE_HIGH_CANCELLATION: 'Tu cuenta ≥50%',
  STORE_ELEVATED_CANCELLATION: 'Cancelación elevada en tu cuenta',
  STORE_LOW_CONFIDENCE: 'Muestra pequeña',
  STORE_CANCELLATION_EVIDENCE: 'Cancelaciones en tu cuenta',
  CUSTOMER_REPLIED: 'Cliente respondió',
  PACKAGE_WITH_DRIVER: 'Con conductor',
};

const attentionStyles = {
  HIGH_ATTENTION: 'border-red-200 bg-red-50 text-red-700',
  REVIEW_RECOMMENDED: 'border-amber-200 bg-amber-50 text-amber-700',
  ELEVATED: 'border-orange-200 bg-orange-50 text-orange-700',
  WAITING_RESPONSE: 'border-sky-200 bg-sky-50 text-sky-700',
  NORMAL: 'border-slate-200 bg-slate-100 text-slate-700',
};

const WORKFLOW_LABELS = {
  REPORTED: 'Reportado a Fast Max',
  ACKNOWLEDGED: 'Visto por operaciones',
  RESOLVED: 'Reporte resuelto',
  REJECTED: 'Reporte no aplicado',
};

const workflowStyles = {
  REPORTED: 'border-violet-200 bg-violet-50 text-violet-700',
  ACKNOWLEDGED: 'border-blue-200 bg-blue-50 text-blue-700',
  RESOLVED: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  REJECTED: 'border-slate-300 bg-slate-100 text-slate-700',
};

const physicalStages = new Set([
  'WITH_DRIVER',
  'OUT_FOR_DELIVERY',
  'POSTPONED_WITH_DRIVER',
]);

export function formatContactDuration(minutes) {
  if (minutes === null || minutes === undefined) return '';
  const safeMinutes = Math.max(Number(minutes) || 0, 0);
  if (safeMinutes < 60) return `${safeMinutes} min`;
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
}

export function AttentionBadge({ level, className = '' }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide font-montserrat ${attentionStyles[level] || attentionStyles.NORMAL} ${className}`}>
      {ATTENTION_LABELS[level] || level}
    </span>
  );
}

export function StoreWorkflowBadge({ workflow }) {
  if (!workflow?.state) return null;
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide font-montserrat ${workflowStyles[workflow.state] || workflowStyles.REPORTED}`}>
      {WORKFLOW_LABELS[workflow.state] || workflow.state}
    </span>
  );
}

export default function DeliveryIntelligenceCard({ item, onOpen }) {
  const delivery = item.delivery || {};
  const communication = item.communication || {};
  const recipient = item.recipient_history || {};
  const storeHistory = item.store_history || {};
  const attention = item.attention || {};
  const workflow = item.store_workflow;
  const physical = physicalStages.has(item.stage);
  const duration = formatContactDuration(communication.minutes_since_outbound);

  return (
    <article className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition-shadow hover:shadow-md ${
      attention.level === 'HIGH_ATTENTION' ? 'border-red-200' : 'border-slate-200'
    }`}>
      <div className={`h-1 ${attention.level === 'HIGH_ATTENTION' ? 'bg-red-500' : 'bg-[#B1336E]'}`} />
      <div className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <AttentionBadge level={attention.level} />
            <StoreWorkflowBadge workflow={workflow} />
            {item.is_active === false && (
              <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[10px] font-extrabold uppercase text-slate-600 font-montserrat">
                Entrega cerrada o reprogramada
              </span>
            )}
          </div>
          <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide font-montserrat ${
            physical
              ? 'border-[#B1336E]/30 bg-[#B1336E]/10 text-[#B1336E]'
              : 'border-slate-200 bg-slate-50 text-slate-600'
          }`}>
            {STAGE_LABELS[item.stage] || item.stage}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-extrabold text-[#B1336E] font-montserrat">
              #{item.delivery_point_id}
            </p>
            <h3 className="mt-1 truncate text-lg font-extrabold text-slate-900 font-montserrat">
              {delivery.recipient_name || 'Destinatario'}
            </h3>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600 font-montserrat">
              <Phone className="h-3.5 w-3.5" />
              {delivery.recipient_phone || 'Sin teléfono'}
            </p>
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500 font-montserrat">
              <CalendarDays className="h-3.5 w-3.5" />
              {delivery.delivery_date
                ? new Date(delivery.delivery_date).toLocaleDateString('es-PE', { timeZone: 'America/Lima' })
                : 'Sin fecha'}
              {delivery.slot ? ` · Slot ${delivery.slot}` : ''}
              {delivery.district ? ` · ${delivery.district}` : ''}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 font-montserrat">
              Comunicación
            </p>
            <p className={`mt-0.5 text-xs font-extrabold font-montserrat ${
              communication.state === 'NO_REPLY' ? 'text-red-700' : 'text-slate-700'
            }`}>
              {COMMUNICATION_LABELS[communication.state] || communication.state}
              {duration ? ` · ${duration}` : ''}
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
            <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 font-montserrat">
              <UserRound className="h-3.5 w-3.5" /> Historial contigo
            </p>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs font-semibold text-slate-700 font-montserrat">
              <span>{recipient.delivered || 0} entregadas</span>
              <span className={recipient.cancelled ? 'text-red-700' : ''}>{recipient.cancelled || 0} canceladas</span>
              <span>{recipient.rejected || 0} rechazadas</span>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
            <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 font-montserrat">
              <Store className="h-3.5 w-3.5" /> Tu cuenta · 90 días
            </p>
            <p className="mt-2 text-sm font-extrabold text-slate-900 font-montserrat">
              {Number(storeHistory.cancellation_rate_pct_90d || 0).toFixed(1)}% cancelación
            </p>
            <p className="text-xs text-slate-500 font-montserrat">
              {storeHistory.terminal_attempts_90d || 0} intentos analizados
            </p>
          </div>
        </div>

        {attention.reason_codes?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {attention.reason_codes.map(code => (
              <span key={code} className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold text-slate-600 font-montserrat">
                {REASON_LABELS[code] || code.split('_').join(' ')}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 rounded-xl border-l-4 border-[#B1336E] bg-[#B1336E]/5 px-3 py-2.5">
          <p className="flex items-start gap-2 text-xs font-semibold leading-5 text-slate-700 font-montserrat">
            {physical ? <PackageCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#B1336E]" /> : <Clock3 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#B1336E]" />}
            {attention.recommendation}
          </p>
        </div>

        <button
          type="button"
          onClick={() => onOpen(item)}
          className="mt-4 w-full rounded-xl bg-[#B1336E] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#9c2d61] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E] focus-visible:ring-offset-2 font-montserrat"
        >
          Ver detalle y reportar
        </button>
      </div>
    </article>
  );
}
